import { Send, SmartToy } from '@mui/icons-material';
import {
    Avatar,
    alpha,
    Box,
    Dialog,
    DialogContent,
    IconButton,
    InputAdornment,
    Paper,
    Slide,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import type { TransitionProps } from '@mui/material/transitions';
import { EChatAgentMessageAuthor } from '@sharedInterfaces/ChatAgent';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import { renderFormattedText } from '@src/App/Components/_UnderConstruction/MvpChatAgent-2/components/TextFormatter';
import { shortcuts } from '@src/App/Components/_UnderConstruction/MvpChatAgent-2/components/types';
import { useChatAgent } from '@src/App/Components/Agents/hooks/useChatAgent';
import type { AppState } from '@store/store';
import { CircleFadingPlus, X as Close } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

interface CompactChatAgentProps {
    open: boolean;
    onClose: () => void;
}

export const CompactChatAgent: React.FC<CompactChatAgentProps> = ({ open, onClose }) => {
    const theme = useTheme();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).chatAgent;

    const [message, setMessage] = useState('');

    const inputRef = useRef<HTMLInputElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);

    // Chat functionality
    const {
        messages,
        isTyping,
        sendMessage: sendChatMessage,
        resetChat,
    } = useChatAgent({
        dummyMessage: langStrings.howCanIHelp,
    });

    // Handle shortcut action
    const handleShortcutAction = useCallback(
        async (action: string) => {
            const success = await sendChatMessage(action);
            if (success) {
                setMessage('');
            }
        },
        [sendChatMessage],
    );

    const handleNewChat = useCallback(() => {
        resetChat();
        setMessage('');
    }, [resetChat]);

    const handleSendMessage = useCallback(async () => {
        const trimmedMessage = message.trim();
        if (!trimmedMessage) return;

        const success = await sendChatMessage(trimmedMessage);
        if (success) {
            setMessage('');
        }
    }, [message, sendChatMessage]);

    const handleKeyPress = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                handleSendMessage();
            }
        },
        [handleSendMessage],
    );

    useEffect(() => {
        if (open && inputRef.current) {
            inputRef.current.focus();
        }
    }, [open]);

    // Scroll to last AI message when messages change
    useEffect(() => {
        if (scrollRef.current && messages.length > 0) {
            // Find last AI message
            const lastAIMessageIndex = messages
                .map((msg, idx) => (msg.role === EChatAgentMessageAuthor.AI ? idx : -1))
                .filter((idx) => idx !== -1)
                .pop();

            if (lastAIMessageIndex !== undefined) {
                // Scroll to last AI message
                const messageElements = scrollRef.current.querySelectorAll('[data-message-index]');
                const targetElement = messageElements[lastAIMessageIndex] as HTMLElement;
                if (targetElement) {
                    targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                } else {
                    // Fallback to bottom scroll
                    scrollRef.current.scrollTo({
                        top: scrollRef.current.scrollHeight,
                        behavior: 'smooth',
                    });
                }
            } else {
                // No AI messages, scroll to bottom
                scrollRef.current.scrollTo({
                    top: scrollRef.current.scrollHeight,
                    behavior: 'smooth',
                });
            }
        }
    }, [messages]);

    if (!open) return null;

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                TransitionComponent={Transition}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        position: 'fixed',
                        bottom: 20,
                        right: 16,
                        top: 'auto',
                        left: 'auto',
                        m: 0,
                        maxHeight: '75vh',
                        width: 400,
                        borderRadius: 2,
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1,
                        borderBottom: 1,
                        borderColor: 'divider',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SmartToy color="primary" />
                        <Typography variant="h6" fontWeight={500}>
                            T4lo Assistant
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                            size="small"
                            onClick={handleNewChat}
                            sx={{ color: theme.palette.primary.main }}
                            title="Neuer Chat"
                        >
                            <CircleFadingPlus size={16} />
                        </IconButton>

                        <IconButton size="small" onClick={onClose}>
                            <Close size={16} />
                        </IconButton>
                    </Box>
                </Box>

                <DialogContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {/* Messages Area */}
                    <Box
                        ref={scrollRef}
                        sx={{
                            flex: 1,
                            overflow: 'auto',
                            p: 2,
                            backgroundColor: alpha(theme.palette.background.default, 0.3),
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                        }}
                    >
                        {messages.length === 0 ? (
                            <Box sx={{ textAlign: 'center', p: 4 }}>
                                <Typography color="textSecondary">
                                    {langStrings.chatEmptyText || 'Stellen Sie eine Frage, um zu beginnen.'}
                                </Typography>
                            </Box>
                        ) : (
                            messages.map((message, idx) => (
                                <Box
                                    key={idx}
                                    data-message-index={idx}
                                    sx={{
                                        display: 'flex',
                                        justifyContent:
                                            message.role === EChatAgentMessageAuthor.USER ? 'flex-end' : 'flex-start',
                                    }}
                                >
                                    {message.role === EChatAgentMessageAuthor.AI && (
                                        <Avatar
                                            sx={{
                                                backgroundColor: theme.palette.primary.main,
                                                width: 28,
                                                height: 28,
                                                mr: 1,
                                                mt: 0.5,
                                            }}
                                        >
                                            <SmartToy fontSize="small" />
                                        </Avatar>
                                    )}
                                    <Paper
                                        sx={{
                                            p: 2,
                                            maxWidth: '85%',
                                            backgroundColor:
                                                message.role === EChatAgentMessageAuthor.USER
                                                    ? theme.palette.primary.main
                                                    : theme.palette.background.paper,
                                            color:
                                                message.role === EChatAgentMessageAuthor.USER
                                                    ? theme.palette.primary.contrastText
                                                    : theme.palette.text.primary,
                                            borderRadius:
                                                message.role === EChatAgentMessageAuthor.USER
                                                    ? '20px 20px 4px 20px'
                                                    : '20px 20px 20px 4px',
                                            boxShadow: theme.shadows[2],
                                        }}
                                    >
                                        {renderFormattedText(
                                            message.text,
                                            message.role === EChatAgentMessageAuthor.USER,
                                        )}
                                    </Paper>
                                </Box>
                            ))
                        )}

                        {isTyping && (
                            <Box sx={{ display: 'flex' }}>
                                <Avatar
                                    sx={{
                                        backgroundColor: theme.palette.primary.main,
                                        width: 28,
                                        height: 28,
                                        mr: 1,
                                        mt: 0.5,
                                    }}
                                >
                                    <SmartToy fontSize="small" />
                                </Avatar>
                                <Paper
                                    sx={{
                                        p: 2,
                                        backgroundColor: theme.palette.background.paper,
                                        borderRadius: '20px 20px 20px 4px',
                                        boxShadow: theme.shadows[2],
                                    }}
                                >
                                    <Typography variant="body2">Schreibt...</Typography>
                                </Paper>
                            </Box>
                        )}

                        {/* QuickLink Buttons */}
                        <Box sx={{ p: 2, pt: 0 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
                                Schnellaktionen:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {shortcuts.map((shortcut, index) => (
                                    <Box
                                        key={index}
                                        onClick={() => handleShortcutAction(shortcut.text)}
                                        sx={{
                                            px: 2,
                                            py: 1,
                                            borderRadius: 2,
                                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                            color: theme.palette.primary.main,
                                            cursor: 'pointer',
                                            fontSize: '0.875rem',
                                            fontWeight: 500,
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                backgroundColor: alpha(theme.palette.primary.main, 0.2),
                                                transform: 'translateY(-1px)',
                                            },
                                        }}
                                    >
                                        {shortcut.text}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Box>

                    {/* Input Area */}
                    <Box
                        sx={{
                            p: 2,
                            borderTop: `1px solid ${theme.palette.divider}`,
                            backgroundColor: theme.palette.background.paper,
                        }}
                    >
                        <TextField
                            ref={inputRef}
                            fullWidth
                            multiline
                            maxRows={3}
                            placeholder={langStrings.inputPlaceholder || 'Was kann ich heute für dich tun?'}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            onKeyPress={handleKeyPress}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    backgroundColor: alpha(theme.palette.background.default, 0.5),
                                },
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={handleSendMessage}
                                            disabled={!message.trim()}
                                            sx={{
                                                backgroundColor: message.trim()
                                                    ? theme.palette.primary.main
                                                    : theme.palette.action.disabled,
                                                color: theme.palette.primary.contrastText,
                                                width: 36,
                                                height: 36,
                                                '&:hover': {
                                                    backgroundColor: message.trim()
                                                        ? theme.palette.primary.dark
                                                        : theme.palette.action.disabled,
                                                },
                                                '&:disabled': {
                                                    color: theme.palette.text.disabled,
                                                },
                                            }}
                                        >
                                            <Send fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};
